import { atom, useRecoilState } from "recoil";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { recoilPersist } from "recoil-persist";
import { Backdrop, Box, Fab, IconButton, Portal, Tooltip, Typography, useMediaQuery } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { overviewCardStyle } from "./backofficeTheme";
import { useCollapse } from "../hooks/useCollapse";
import { useThemeColor } from "../hooks/useThemeColor";
import { useDialog } from "../hooks/useDialog";

const { persistAtom } = recoilPersist();
export const rightSidebarCollapsibleStateAtom = atom<"expanded" | "collapsed" | "unset">({
  key: "rightSidebarCollapsibleStateAtom",
  default: "unset",
  effects: [persistAtom],
});

export function RightCollapsibleSidebar(props: {
  children: React.ReactNode;
  expandedSx?: object;
  drawerBelowContainerWidth?: number;
  title?: string;
  variant?: "responsive" | "overlay";
}) {
  const { children, expandedSx, drawerBelowContainerWidth, title, variant = "responsive" } = props;
  const isSmallScreen = useMediaQuery("(max-width: 1400px)");
  const containerMarkerRef = useRef<HTMLSpanElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const drawerTriggerRef = useRef<HTMLButtonElement>(null);
  const drawerCloseButtonRef = useRef<HTMLButtonElement>(null);
  const drawerTitleId = useId();
  const [useDrawerForContainer, setUseDrawerForContainer] = useState(false);
  const { dialogProps, isDialogOpen: isDrawerOpen, openDialog, closeDialog: closeDrawer } = useDialog();
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const useDrawer = variant === "overlay" || useDrawerForContainer;

  const [sidebarPermanentState, setSidebarPermanentState] = useRecoilState(rightSidebarCollapsibleStateAtom);
  const { isExpanded, expand, collapse } = useCollapse(
    () => sidebarPermanentState === "collapsed" || (sidebarPermanentState === "unset" && isSmallScreen),
  );

  const onClick = useCallback(() => {
    if (isExpanded) {
      collapse();
    } else {
      expand();
    }
    setSidebarPermanentState(isExpanded ? "collapsed" : "expanded");
  }, [isExpanded, collapse, expand, setSidebarPermanentState]);

  const openDrawer = useCallback(() => {
    setIsDrawerVisible(true);
    openDialog();
  }, [openDialog]);

  useEffect(() => {
    const container = containerMarkerRef.current?.parentElement;
    if (variant === "overlay" || !container || !drawerBelowContainerWidth || !globalThis.ResizeObserver) {
      return;
    }

    const updateMode = () =>
      setUseDrawerForContainer(container.getBoundingClientRect().width < drawerBelowContainerWidth);
    const resizeObserver = new ResizeObserver(updateMode);
    resizeObserver.observe(container);
    updateMode();
    return () => resizeObserver.disconnect();
  }, [drawerBelowContainerWidth, variant]);

  useEffect(() => {
    closeDrawer();
    if (!useDrawer) {
      setIsDrawerVisible(false);
    }
  }, [useDrawer, closeDrawer]);

  useEffect(() => {
    if (!useDrawer || !isDrawerOpen) {
      return;
    }

    drawerCloseButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      const sidebar = sidebarRef.current;
      if (!sidebar?.contains(document.activeElement)) {
        return;
      }
      if (event.key === "Escape") {
        closeDrawer();
        return;
      }
      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = Array.from(
        sidebar.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.getClientRects().length);
      const firstElement = focusableElements.at(0);
      const lastElement = focusableElements.at(-1);
      if (!firstElement || !lastElement) {
        event.preventDefault();
        sidebar.focus();
      } else if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [useDrawer, isDrawerOpen, closeDrawer]);

  const handleDrawerTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget || event.propertyName !== "transform" || isDrawerOpen) {
        return;
      }
      setIsDrawerVisible(false);
      requestAnimationFrame(() => drawerTriggerRef.current?.focus());
    },
    [isDrawerOpen],
  );

  return (
    <>
      <span
        ref={containerMarkerRef}
        className={useDrawer ? "RightCollapsibleSidebar-drawerModeMarker" : undefined}
        style={{ display: "none" }}
      />
      <Portal container={() => containerMarkerRef.current?.closest<HTMLElement>(".MuiModal-root") ?? document.body}>
        {useDrawer && (
          <Tooltip title="Dokumente & Notizen öffnen">
            <Fab
              ref={drawerTriggerRef}
              color="primary"
              onClick={openDrawer}
              data-testid="open-notes-card"
              aria-label="Dokumente & Notizen öffnen"
              aria-hidden={isDrawerVisible ? true : undefined}
              sx={(theme) => ({
                position: "fixed",
                right: { xs: "16px", sm: "24px" },
                bottom: { xs: "16px", sm: "24px" },
                zIndex: theme.zIndex.drawer,
                visibility: isDrawerVisible ? "hidden" : "visible",
              })}
            >
              <ArticleOutlinedIcon />
            </Fab>
          </Tooltip>
        )}
      </Portal>
      <Portal
        container={() => containerMarkerRef.current?.closest<HTMLElement>(".MuiModal-root") ?? document.body}
        disablePortal={!useDrawer}
      >
        <>
          <Backdrop
            open={useDrawer && isDrawerOpen}
            onClick={closeDrawer}
            sx={(theme) => ({ zIndex: theme.zIndex.modal })}
          />
          <Box
            className={useDrawer && isDrawerVisible ? "RightCollapsibleSidebar-drawerOpen" : undefined}
            ref={sidebarRef}
            role={useDrawer ? "dialog" : undefined}
            aria-label={useDrawer && !title ? "Dokumente & Notizen" : undefined}
            aria-labelledby={useDrawer && title ? drawerTitleId : undefined}
            aria-modal={useDrawer && isDrawerVisible ? true : undefined}
            aria-hidden={useDrawer && !isDrawerVisible ? true : undefined}
            tabIndex={useDrawer ? -1 : undefined}
            onTransitionEnd={handleDrawerTransitionEnd}
            sx={
              useDrawer
                ? (theme) => ({
                    boxSizing: "border-box",
                    position: "fixed",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: { xs: "100vw", sm: "340px", md: "440px" },
                    maxWidth: "100vw",
                    pt: title ? 0 : "48px",
                    zIndex: theme.zIndex.modal + 1,
                    overflowY: "auto",
                    backgroundColor: "background.paper",
                    boxShadow: theme.shadows[16],
                    transform: isDrawerOpen ? "translateX(0)" : "translateX(100%)",
                    visibility: isDrawerVisible ? "visible" : "hidden",
                    transition: theme.transitions.create("transform", {
                      easing: theme.transitions.easing.sharp,
                      duration: isDrawerOpen
                        ? theme.transitions.duration.enteringScreen
                        : theme.transitions.duration.leavingScreen,
                    }),
                  })
                : {
                    position: "relative",
                    ...(isExpanded ? overviewCardStyle : {}),
                    p: 0,
                    flex: isExpanded ? 1 : "0 0 0px",
                    marginLeft: 0,
                    marginRight: 0,
                    alignSelf: "flex-start",
                    height: "auto",
                    width: isExpanded ? "auto" : 0,
                    minWidth: 0,
                    overflow: "visible",
                    transition: "width 0.25s ease-in-out",
                    ...(isExpanded ? {} : { background: "transparent", boxShadow: "none" }),
                    ...(isExpanded ? expandedSx : {}),
                  }
            }
          >
            {useDrawer && title ? (
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minHeight: "64px",
                  boxSizing: "border-box",
                  py: 1.5,
                  pl: 3,
                  pr: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography
                  id={drawerTitleId}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: "text.primary",
                    fontSize: "15px",
                    fontWeight: 600,
                    overflowWrap: "anywhere",
                  }}
                >
                  {title}
                </Typography>
                <IconButton
                  ref={drawerCloseButtonRef}
                  onClick={closeDrawer}
                  data-testid="close-notes-card"
                  aria-label="Dokumente & Notizen schließen"
                  sx={{ flexShrink: 0, color: "text.disabled" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : useDrawer ? (
              <IconButton
                ref={drawerCloseButtonRef}
                onClick={closeDrawer}
                data-testid="close-notes-card"
                aria-label="Dokumente & Notizen schließen"
                sx={{ position: "absolute", top: "16px", right: "28px", color: "text.disabled", zIndex: 1 }}
              >
                <CloseIcon />
              </IconButton>
            ) : (
              <ArrowIcon isExpanded={isExpanded} onClick={onClick} />
            )}
            <Box key={dialogProps.key} sx={{ display: useDrawer || isExpanded ? "block" : "none" }}>
              {children}
            </Box>
          </Box>
        </>
      </Portal>
    </>
  );
}

function ArrowIcon(props: { isExpanded: boolean; onClick: () => void }) {
  const { isExpanded, onClick } = props;
  const primaryColor = useThemeColor("primary");

  if (!isExpanded) {
    return (
      <Box
        component="button"
        type="button"
        onClick={onClick}
        data-testid="open-notes-card"
        sx={{
          border: "none",
          cursor: "pointer",
          position: "absolute",
          top: "194px",
          right: "auto",
          left: "2px",
          transform: "rotate(-90deg)",
          transformOrigin: "top left",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          paddingLeft: "8px",
          paddingRight: "12px",
          paddingTop: "10px",
          paddingBottom: "7px",
          width: "auto",
          height: "auto",
          borderRadius: "8px 8px 0 0",
          zIndex: 2,
          whiteSpace: "nowrap",
          backgroundColor: "background.paper",
          color: "text.primary",
          boxShadow: "var(--backoffice-shadow) 0 2px 8px",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <ExpandLessIcon sx={{ fontSize: "18px", transform: "rotate(360deg)", color: primaryColor.main }} />
        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary" }}>Dokumente & Notizen</Typography>
      </Box>
    );
  }

  return (
    <IconButton
      onClick={onClick}
      data-testid="close-notes-card"
      aria-label="Dokumente & Notizen schließen"
      sx={{
        width: "32px",
        height: "32px",
        transform: isExpanded ? "rotate(90deg)" : "rotate(-90deg)",
        transition: "transform 0.3s ease",
        borderRadius: "50%",
        position: "absolute",
        top: "25px",
        left: "-14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1,
        backgroundColor: primaryColor.main,
        "&:hover": {
          backgroundColor: primaryColor.dark,
        },
      }}
    >
      <ExpandLessIcon sx={{ fontSize: "20px", color: primaryColor.contrastText }} />
    </IconButton>
  );
}
